import axios from 'axios';
import { getTranslation } from './translations';

/**
 *
 * @param {string} password
 * @param {string} email
 * @param {string} userSub
 * @returns Promise<{sdkToken: {string}, applicantId: {string}, metaData: *}>
 */
export const fetchSdkToken = async (email, userSub, password) => {
  const identityscheme = 'manualid';
  const nameidentifier = userSub.split('|')[1];
  const { data } = await axios.post('api/document-verification', {
    password,
    email,
    identityscheme,
    nameidentifier,
  });
  return data;
};

export const refreshSdkToken = async (applicantId) => {
  const { data } = await axios.post(`api/refresh-token/${applicantId}`);
  return data;
};

/**
 *
 * @param {string} applicantId
 * @returns {Promise<{checkId: string, metaData: *}>}
 */
export const createDocumentCheck = async (applicantId) => {
  const { data } = await axios.post(
    `api/document-verification/${applicantId}/check`,
  );
  return data;
};

/**
 * Create a document check using Cognito JWT
 * @param {string} applicantId
 * @param {string} jwt
 * @returns {Promise<{checkId: string, metaData: *}>}
 */
export const createDocumentCheckCognito = async (applicantId, jwt) => {
  const { data } = await axios.post(
    `api/document-verification/${applicantId}/check-cognito`,
    null,
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    },
  );
  return data;
};


/**
 *
 * @param {string} applicantId
 * @returns {Promise<boolean>}
 */
export const getDocumentCheck = async (applicantId) => {
  const { data } = await axios.get(
    `api/document-verification/${applicantId}/check`,
  );
  return data;
};

/**
 * Get the latest document check status by asking ID Auth if the customer has been fully verified
 * by OnFido yet or not. Might result failed results if the customer provided documents that needs
 * to be verified by humans or unacceptable country.
 *
 * @param {string} customerJwt
 * @returns {Promise<{isVerified: boolean, consider: *, countryCheck: *}>}
 */
export const getDocumentCheckCognito = async (customerJwt) => {
  try {
    const { data } = await axios.get(
      `api/document-verification/check-cognito`,
      {
        headers: {
          Authorization: `Bearer ${customerJwt}`,
        },
      },
    );

    return data;
  } catch (error) {
    const isUnknownOrNotVerified = error.response && error.response.status === 404;
    if (isUnknownOrNotVerified) {
      return {
        isVerified: false,
        consider: [],
        countryCheck: null,
      };
    }

    console.error('Error when trying to retrieve verification status from ID Auth, responding with 500:', error);
    throw error;
  }
};

/**
 *
 * @param {string} applicantId
 * @param {number} retries
 * @param {number} interval
 * @param {number} retryCounter
 * @returns {Promise<boolean>}
 */
export const pollDocumentCheck = async (
  applicantId,
  retries = 72,
  interval = 2.5, // 2.5 * 72 = 180 seconds = 3 minutes the user should wait to be verified or not
  retryCounter = 0,
) => {
  const { isVerified, consider, countryCheck } = await getDocumentCheck(
    applicantId,
  );
  if (consider && consider.length > 0) {
    throw new Error('Verification reports failed');
  }
  if (countryCheck && !countryCheck.isValidCountry) {
    const countryError = getTranslation('countryErrorMessage', { countryName: countryCheck.countryName });
    throw new Error(countryError);
  }
  if (isVerified || retryCounter > retries) {
    return isVerified;
  }
  await new Promise((resolve) => setTimeout(resolve, interval * 1000));
  return pollDocumentCheck(applicantId, retries, interval, retryCounter + 1);
};

/**
 * Checks latest document status by asking ID Auth if the customer has been fully verified
 * by OnFido yet or not.
 *
 * @param {string} applicantId
 * @param {string} jwt
 * @param {number} retries
 * @param {number} interval
 * @param {number} retryCounter
 * @returns {Promise<boolean>}
 */
export const pollDocumentCheckCognito = async (
  applicantId,
  jwt,
  retries = 72,
  interval = 2.5, // 2.5 * 72 = 180 seconds = 3 minutes the user should wait to be verified or not
  retryCounter = 0,
) => {
  const { isVerified, consider, countryCheck } = await getDocumentCheckCognito(
    applicantId,
    jwt,
  );
  if (consider && consider.length > 0) {
    throw new Error('Verification reports failed');
  }
  if (countryCheck && !countryCheck.isValidCountry) {
    const countryError = getTranslation('countryErrorMessage', { countryName: countryCheck.countryName });
    throw new Error(countryError);
  }
  if (isVerified || retryCounter > retries) {
    return isVerified;
  }
  await new Promise((resolve) => setTimeout(resolve, interval * 1000));
  return pollDocumentCheckCognito(applicantId, retries, interval, retryCounter + 1);
};

